/** @format */

import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import { createPortal } from "react-dom";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  PictureInPicture2,
  Gauge,
  SkipBack,
  SkipForward,
  MessageSquare,
} from "lucide-react";
import type {
  MediaTranscription,
  TranscriptionSegment,
} from "../../api/sopMedia";

export interface HighlightSegment {
  startTime: number;
  endTime: number;
  active?: boolean;
  /** Shown on hover over the segment in the seek bar */
  title?: string;
}

export interface MediaPlayerHandle {
  seekTo: (time: number) => void;
  play: () => Promise<void>;
}

export interface MediaPlayerProps {
  src: string;
  variant: "video" | "audio";
  className?: string;
  poster?: string;
  title?: string;
  transcription?: MediaTranscription | null;
  initialTime?: number;
  onTimeUpdate?: (currentTime: number) => void;
  /** Highlight segments rendered on the seek bar */
  highlightSegments?: HighlightSegment[];
  /** When set, player seeks to this time on change */
  seekToTime?: number;
}

function formatTimestamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function getSegmentState(
  segment: TranscriptionSegment,
  currentTime: number,
): "past" | "active" | "future" {
  if (currentTime < segment.start_time) return "future";
  if (currentTime >= segment.end_time) return "past";
  return "active";
}

const PERSIST_DEBOUNCE_MS = 1000;
const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

export const MediaPlayer = forwardRef<MediaPlayerHandle, MediaPlayerProps>(
  function MediaPlayer(
    {
      src,
      variant,
      className = "",
      poster,
      title,
      transcription,
      initialTime = 0,
      onTimeUpdate,
      highlightSegments,
      seekToTime,
    },
    ref,
  ) {
    const [error, setError] = useState<string | null>(null);
    const [currentTime, setCurrentTime] = useState(initialTime);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isPiP, setIsPiP] = useState(false);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const [showSpeedMenu, setShowSpeedMenu] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [pipSupported, setPipSupported] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showTranscript, setShowTranscript] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      setPipSupported(
        typeof document !== "undefined" && !!document.pictureInPictureEnabled,
      );
    }, []);

    useEffect(() => {
      const handler = () => setIsFullscreen(!!document.fullscreenElement);
      document.addEventListener("fullscreenchange", handler);
      return () => document.removeEventListener("fullscreenchange", handler);
    }, []);

    const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const seekBarRef = useRef<HTMLDivElement>(null);
    const [hoverInfo, setHoverInfo] = useState<{
      clientX: number;
      barTop: number;
      time: number;
    } | null>(null);

    useImperativeHandle(ref, () => ({
      seekTo: (time: number) => {
        const video = videoRef.current;
        if (!video) return;
        video.currentTime = time;
        setCurrentTime(time);
      },
      play: () => videoRef.current?.play() ?? Promise.resolve(),
    }));

    useEffect(() => {
      if (seekToTime == null || !videoRef.current) return;
      videoRef.current.currentTime = seekToTime;
      setCurrentTime(seekToTime);
    }, [seekToTime]);

    const handleError = useCallback(() => {
      setError("Failed to load media");
    }, []);

    const handleLoadedData = useCallback(() => {
      setError(null);
      const video = videoRef.current;
      if (!video) return;
      setDuration(video.duration || 0);
      if (initialTime > 0 && video.currentTime !== initialTime) {
        video.currentTime = initialTime;
        setCurrentTime(initialTime);
      }
    }, [initialTime]);

    const handleLoadedMetadata = useCallback(() => {
      const video = videoRef.current;
      if (!video) return;
      setDuration(video.duration || 0);
      setVolume(video.volume);
      setPlaybackRate(video.playbackRate);
      setIsMuted(video.muted);
    }, []);

    const persistTime = useCallback(() => {
      if (!onTimeUpdate || !videoRef.current) return;
      onTimeUpdate(videoRef.current.currentTime);
    }, [onTimeUpdate]);

    const schedulePersist = useCallback(() => {
      if (!onTimeUpdate) return;
      if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
      persistTimeoutRef.current = setTimeout(() => {
        persistTimeoutRef.current = null;
        persistTime();
      }, PERSIST_DEBOUNCE_MS);
    }, [onTimeUpdate, persistTime]);

    const handleTimeUpdate = useCallback(() => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
        schedulePersist();
      }
    }, [schedulePersist]);

    const handleSeeked = useCallback(() => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
        persistTime();
      }
    }, [persistTime]);

    useEffect(() => {
      const video = videoRef.current;
      return () => {
        if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
        if (onTimeUpdate && video) onTimeUpdate(video.currentTime);
      };
    }, [onTimeUpdate]);

    const handlePlayPause = useCallback(() => {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }, []);

    const handlePlay = useCallback(() => setIsPlaying(true), []);
    const handlePause = useCallback(() => setIsPlaying(false), []);

    const handleMuteToggle = useCallback(() => {
      const video = videoRef.current;
      if (!video) return;
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }, []);

    const handleVolumeChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = parseFloat(e.target.value);
        const video = videoRef.current;
        if (!video) return;
        video.volume = v;
        video.muted = v === 0;
        setVolume(v);
        setIsMuted(v === 0);
      },
      [],
    );

    const setRate = useCallback((rate: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.playbackRate = rate;
      setPlaybackRate(rate);
      setShowSpeedMenu(false);
    }, []);

    const handlePiP = useCallback(async () => {
      const video = videoRef.current;
      if (!video || !document.pictureInPictureEnabled) return;
      try {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
          setIsPiP(false);
        } else {
          await video.requestPictureInPicture();
          setIsPiP(true);
        }
      } catch {
        // ignore
      }
    }, []);

    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;
      const onLeavePiP = () => setIsPiP(false);
      video.addEventListener("leavepictureinpicture", onLeavePiP);
      return () => {
        video.removeEventListener("leavepictureinpicture", onLeavePiP);
      };
    }, []);

    const handleFullscreen = useCallback(() => {
      const el = containerRef.current;
      if (!el) return;
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (el.requestFullscreen) {
        el.requestFullscreen();
      }
    }, []);

    /** Seek to the time under the click (bar or segment — clicking a segment seeks to that point, not to segment start) */
    const handleSeekToClickPosition = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        const bar = seekBarRef.current;
        const video = videoRef.current;
        if (!bar || !video || !duration) return;
        const rect = bar.getBoundingClientRect();
        const fraction = Math.max(
          0,
          Math.min(1, (e.clientX - rect.left) / rect.width),
        );
        video.currentTime = fraction * duration;
      },
      [duration],
    );

    const resetControlsTimeout = useCallback(() => {
      setShowControls(true);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = setTimeout(() => {
        if (videoRef.current && !videoRef.current.paused)
          setShowControls(false);
      }, 3000);
    }, []);

    useEffect(() => {
      return () => {
        if (controlsTimeoutRef.current)
          clearTimeout(controlsTimeoutRef.current);
      };
    }, []);

    const handleSeekBarMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        const bar = seekBarRef.current;
        if (!bar || !duration) return;
        const rect = bar.getBoundingClientRect();
        const fraction = Math.max(
          0,
          Math.min(1, (e.clientX - rect.left) / rect.width),
        );
        setHoverInfo({
          clientX: e.clientX,
          barTop: rect.top,
          time: fraction * duration,
        });
      },
      [duration],
    );

    const handleSeekBarMouseLeave = useCallback(() => {
      setHoverInfo(null);
    }, []);

    const segments =
      transcription?.status === "completed" && transcription.segments
        ? transcription.segments
        : [];
    const activeSegmentRef = useRef<HTMLButtonElement | null>(null);
    const activeIndex = segments.findIndex(
      (s) => s.start_time <= currentTime && currentTime < s.end_time,
    );

    useEffect(() => {
      if (activeIndex >= 0 && activeSegmentRef.current) {
        activeSegmentRef.current.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }, [activeIndex]);

    const handleSegmentClick = useCallback((segment: TranscriptionSegment) => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = segment.start_time;
      video.play().catch(() => {});
    }, []);

    const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

    /** Section (highlight segment) that contains currentTime; used for the island and prev/next */
    const sectionSegments = useMemo(
      () => highlightSegments ?? [],
      [highlightSegments],
    );
    const currentSectionIndex = sectionSegments.findIndex(
      (seg) => seg.startTime <= currentTime && currentTime < seg.endTime,
    );
    const currentSection =
      currentSectionIndex >= 0
        ? (sectionSegments[currentSectionIndex] ?? null)
        : null;
    /** First section that starts after currentTime (next section to jump to) */
    const nextSection = useMemo(
      () => sectionSegments.find((seg) => seg.startTime > currentTime) ?? null,
      [sectionSegments, currentTime],
    );
    /** Last section that starts before currentTime (previous section to jump to) */
    const prevSection = useMemo(() => {
      const before = sectionSegments.filter(
        (seg) => seg.startTime < currentTime,
      );
      return before.length > 0 ? before[before.length - 1] : null;
    }, [sectionSegments, currentTime]);
    const hasNextSection = nextSection != null;

    const seekTo = useCallback((time: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = time;
      setCurrentTime(time);
    }, []);

    const goToPrevSection = useCallback(() => {
      if (prevSection) seekTo(prevSection.startTime);
      else seekTo(0);
    }, [prevSection, seekTo]);

    const goToNextSection = useCallback(() => {
      if (nextSection) seekTo(nextSection.startTime);
    }, [nextSection, seekTo]);

    if (variant === "video") {
      return (
        <div
          className={`flex flex-col flex-1 min-h-0 bg-slate-900 rounded-lg overflow-hidden ${className}`}
        >
          {/* Video area with custom controls */}
          <div
            ref={containerRef}
            className={`relative flex-1 min-h-[200px] flex flex-col bg-black/40 ${isFullscreen ? "" : "shrink-0"}`}
            onMouseMove={resetControlsTimeout}
            onMouseEnter={() => setShowControls(true)}
          >
            {error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-red-400 text-sm">
                {error}
                <a
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-slate-200 underline"
                >
                  Open in new tab
                </a>
              </div>
            )}
            {!error && (
              <>
                {/* Island: section title only; visible on video hover when inside a section, with fade */}
                {currentSection && (
                  <div
                    className={`absolute top-3 left-1/2 -translate-x-1/2 z-10 px-4 py-2 rounded-full bg-black/70 backdrop-blur-xl border border-white/10 shadow-lg transition-opacity duration-300 ${
                      showControls
                        ? "opacity-100"
                        : "opacity-0 pointer-events-none"
                    }`}
                    role="status"
                    aria-label={`Section: ${currentSection.title ?? "Current section"}`}
                  >
                    <span className="text-sm font-medium text-white/95 max-w-[220px] truncate block">
                      {currentSection.title ?? "Section"}
                    </span>
                  </div>
                )}

                <video
                  ref={videoRef}
                  src={src}
                  poster={poster}
                  className={`w-full flex-1 min-h-0 ${isFullscreen ? "" : "max-h-[70vh]"} object-contain cursor-pointer`}
                  onError={handleError}
                  onLoadedData={handleLoadedData}
                  onLoadedMetadata={handleLoadedMetadata}
                  onTimeUpdate={handleTimeUpdate}
                  onSeeked={handleSeeked}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onClick={handlePlayPause}
                  preload="metadata"
                  playsInline
                  title={title}
                />

                {/* Custom controls overlay */}
                <div
                  className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-8 pb-2 px-3 transition-opacity duration-300 ${
                    showControls
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none"
                  }`}
                >
                  {/* Seek bar */}
                  <div
                    ref={seekBarRef}
                    className="relative h-3 group cursor-pointer mb-2"
                    onClick={handleSeekToClickPosition}
                    onMouseMove={(e) => {
                      resetControlsTimeout();
                      handleSeekBarMouseMove(e);
                    }}
                    onMouseLeave={handleSeekBarMouseLeave}
                    onMouseEnter={() => setShowControls(true)}
                    role="slider"
                    aria-label="Video progress"
                    aria-valuenow={Math.round(currentTime)}
                    aria-valuemin={0}
                    aria-valuemax={Math.round(duration)}
                  >
                    {/* Track background */}
                    <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1.5 group-hover:h-2 rounded-full bg-white/25 transition-all" />

                    {/* All step segments: hover shows title, click seeks to start */}
                    {duration > 0 &&
                      highlightSegments?.map((seg, i) => {
                        const startPct = (seg.startTime / duration) * 100;
                        const widthPct =
                          ((seg.endTime - seg.startTime) / duration) * 100;
                        const isActive = seg.active;
                        return (
                          <button
                            key={i}
                            type="button"
                            className="absolute top-1/2 -translate-y-1/2 h-1.5 group-hover:h-2 transition-all cursor-pointer border-0 p-0 align-baseline z-[1]"
                            style={{
                              left: `${startPct}%`,
                              width: `${widthPct}%`,
                              backgroundColor: isActive
                                ? "rgba(56, 189, 248, 0.75)"
                                : "rgba(56, 189, 248, 0.15)",
                              borderLeft: `2px solid #777`,
                            }}
                            onClick={handleSeekToClickPosition}
                            title={seg.title ?? undefined}
                            aria-label={
                              seg.title
                                ? `Step: ${seg.title}. Click to go to this part.`
                                : "Step segment"
                            }
                          />
                        );
                      })}

                    {/* Tooltip: rendered in document.body with fixed position so it is not clipped by overflow */}
                    {hoverInfo &&
                      createPortal(
                        <div
                          className="fixed z-50 pointer-events-none px-2.5 py-1.5 rounded-md bg-slate-800/95 border border-slate-600/60 text-white text-xs font-medium shadow-lg whitespace-nowrap"
                          style={{
                            left: hoverInfo.clientX,
                            top: Math.max(20, hoverInfo.barTop - 8),
                            transform: "translate(-50%, -100%)",
                          }}
                        >
                          {formatTimestamp(hoverInfo.time)}
                        </div>,
                        document.body,
                      )}

                    {/* Progress fill — neutral to match dark player */}
                    <div
                      className="absolute top-1/2 -translate-y-1/2 left-0 h-1.5 group-hover:h-2 rounded-full bg-white/90 transition-all pointer-events-none"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>

                  {/* Controls row */}
                  <div className="flex items-center gap-1 sm:gap-2 text-white/90">
                    <button
                      type="button"
                      onClick={goToPrevSection}
                      className="p-1.5 rounded hover:bg-white/10 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent"
                      aria-label="Previous section or start of video"
                      title="Previous section"
                    >
                      <SkipBack className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handlePlayPause}
                      className="p-1.5 rounded hover:bg-white/10 hover:text-white transition-colors"
                      aria-label={isPlaying ? "Pause" : "Play"}
                    >
                      {isPlaying ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={goToNextSection}
                      disabled={!hasNextSection}
                      className="p-1.5 rounded hover:bg-white/10 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent"
                      aria-label="Next section"
                      title="Next section"
                    >
                      <SkipForward className="w-4 h-4" />
                    </button>

                    {/* Volume */}
                    <div
                      className="relative flex items-center"
                      onMouseEnter={() => setShowVolumeSlider(true)}
                      onMouseLeave={() => setShowVolumeSlider(false)}
                    >
                      <button
                        type="button"
                        onClick={handleMuteToggle}
                        className="p-1.5 rounded hover:bg-white/10 hover:text-white transition-colors"
                        aria-label={isMuted ? "Unmute" : "Mute"}
                      >
                        {isMuted || volume === 0 ? (
                          <VolumeX className="w-4 h-4" />
                        ) : (
                          <Volume2 className="w-4 h-4" />
                        )}
                      </button>
                      {showVolumeSlider && (
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={isMuted ? 0 : volume}
                          onChange={handleVolumeChange}
                          className="w-16 h-1 accent-white/90 bg-white/20 rounded cursor-pointer ml-0.5"
                          aria-label="Volume"
                        />
                      )}
                    </div>

                    <span className="text-xs tabular-nums text-white/70 select-none shrink-0">
                      {formatTimestamp(currentTime)} /{" "}
                      {formatTimestamp(duration)}
                    </span>

                    <div className="flex-1 min-w-2" />

                    {/* Transcript toggle */}
                    {segments.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowTranscript((v) => !v)}
                        className={`p-1.5 rounded transition-colors ${showTranscript ? "bg-white/20 text-white" : "hover:bg-white/10 hover:text-white text-white/90"}`}
                        aria-label="Toggle transcript"
                        title="Transcript"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                    )}

                    {/* Playback speed */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowSpeedMenu((v) => !v)}
                        className="flex items-center gap-1 p-1.5 rounded hover:bg-white/10 hover:text-white transition-colors text-xs tabular-nums min-w-[2.5rem]"
                        aria-label="Playback speed"
                        title="Speed"
                      >
                        <Gauge className="w-3.5 h-3.5 opacity-80" />
                        <span>{playbackRate}x</span>
                      </button>
                      {showSpeedMenu && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            aria-hidden
                            onClick={() => setShowSpeedMenu(false)}
                          />
                          <div className="absolute bottom-full left-0 mb-1 py-1 rounded-lg bg-slate-800/95 border border-slate-600/50 shadow-xl z-20 min-w-[4rem]">
                            {PLAYBACK_RATES.map((r) => (
                              <button
                                key={r}
                                type="button"
                                onClick={() => setRate(r)}
                                className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-white/10 ${
                                  playbackRate === r
                                    ? "text-sky-300 font-medium"
                                    : "text-white/90"
                                }`}
                              >
                                {r}x
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    {pipSupported && (
                      <button
                        type="button"
                        onClick={handlePiP}
                        className={`p-1.5 rounded hover:bg-white/10 hover:text-white transition-colors ${isPiP ? "text-sky-300" : ""}`}
                        aria-label="Picture-in-Picture"
                        title="Picture-in-Picture"
                      >
                        <PictureInPicture2 className="w-4 h-4" />
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={handleFullscreen}
                      className="p-1.5 rounded hover:bg-white/10 hover:text-white transition-colors"
                      aria-label="Fullscreen"
                    >
                      <Maximize className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Transcript panel */}
          {transcription && !error && (
            <div className="flex flex-col min-h-0 border-t border-slate-600/50 bg-slate-800/60">
              {transcription.status === "in_progress" && (
                <p className="px-4 py-3 text-sm text-slate-500">
                  Transcription in progress…
                </p>
              )}
              {transcription.status === "failed" && (
                <p className="px-4 py-3 text-sm text-amber-500/90">
                  Transcription unavailable.
                </p>
              )}
              {showTranscript &&
                transcription?.status === "completed" &&
                segments.length > 0 && (
                  <>
                    <div className="px-4 py-2 border-b border-slate-600/50 shrink-0">
                      <span className="text-xs font-medium uppercase tracking-wider text-slate-500">
                        Transcript
                      </span>
                    </div>
                    <div
                      className="overflow-y-auto flex-1 min-h-0 max-h-[240px] py-1 text-sm leading-relaxed"
                      role="region"
                      aria-label="Video transcript"
                    >
                      {segments.map((segment, i) => {
                        const state = getSegmentState(segment, currentTime);
                        const isActive = state === "active";
                        return (
                          <button
                            type="button"
                            key={`${segment.start_time}-${i}`}
                            ref={isActive ? activeSegmentRef : undefined}
                            data-start={segment.start_time}
                            data-end={segment.end_time}
                            onClick={() => handleSegmentClick(segment)}
                            className={`w-full text-left flex gap-3 px-4 py-2 rounded-none transition-colors duration-150 border-l-2 border-transparent hover:bg-slate-700/40 ${
                              isActive
                                ? "border-sky-400 bg-sky-500/10 text-slate-100"
                                : state === "past"
                                  ? "text-slate-500/90"
                                  : "text-slate-400"
                            }`}
                          >
                            <span
                              className={`shrink-0 text-xs tabular-nums mt-0.5 ${
                                isActive ? "text-sky-400" : "text-slate-500"
                              }`}
                            >
                              {formatTimestamp(segment.start_time)}
                            </span>
                            <span
                              className={isActive ? "font-medium" : undefined}
                            >
                              {segment.text}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        className={`flex flex-col flex-1 min-h-0 bg-slate-900 rounded-lg overflow-hidden p-4 ${className}`}
      >
        {error && (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-red-400 text-sm">
            {error}
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-slate-200 underline"
            >
              Open in new tab
            </a>
          </div>
        )}
        {!error && (
          <audio
            src={src}
            controls
            controlsList="nodownload"
            className="w-full"
            onError={handleError}
            onLoadedData={handleLoadedData}
            preload="metadata"
            title={title}
          />
        )}
      </div>
    );
  },
);
